import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    DndContext, DragEndEvent, PointerSensor, useSensor, useSensors, Modifier,
} from '@dnd-kit/core';
import {
    Plus, Settings, BarChart2, Loader2, Search, X, ExternalLink,
    GitBranch, Clock, CheckCircle2, Mail, ZoomIn, ZoomOut, ScrollText, AlertTriangle,
} from 'lucide-react';
import { GlassButton, GlassBadge } from '../ui';
import {
    getJourney, saveJourney, getEDMSequences, getJourneyAnalytics, getCampaign,
    getProjectJourney, saveProjectJourney,
    JourneyConfig, JourneyNode as JourneyNodeType, JourneyEdge,
} from '../../services/api';
import JourneyNodeComponent, { NODE_WIDTH, NODE_HEIGHT } from './JourneyNode';
import JourneyEdgePanel from './JourneyEdgePanel';
import JourneyEventLog from './JourneyEventLog';
import './JourneyCanvas.css';

const ENTRY_NODE_ID = '__entry__';
const ENTRY_W = 180;
const ENTRY_H = 44;
const CANVAS_PAD = 80;

const SCALE_MIN  = 0.25;
const SCALE_MAX  = 2.0;
const SCALE_STEP = 0.1;

// ── Bezier path helper ───────────────────────────────────────────────────────

function bezierPath(sx: number, sy: number, tx: number, ty: number): string {
    const dy = Math.abs(ty - sy);
    const cp = Math.max(80, dy * 0.5);
    return `M ${sx} ${sy} C ${sx} ${sy + cp}, ${tx} ${ty - cp}, ${tx} ${ty}`;
}

function midpoint(sx: number, sy: number, tx: number, ty: number) {
    // Approximate midpoint on the cubic bezier at t=0.5
    const dy = Math.abs(ty - sy);
    const cp = Math.max(80, dy * 0.5);
    const t = 0.5;
    const mt = 1 - t;
    const bx = mt * mt * mt * sx + 3 * mt * mt * t * sx + 3 * mt * t * t * tx + t * t * t * tx;
    const cp1y = sy + cp;
    const cp2y = ty - cp;
    const by = mt * mt * mt * sy + 3 * mt * mt * t * cp1y + 3 * mt * t * t * cp2y + t * t * t * ty;
    return { x: bx, y: by };
}

function edgeColour(edge: JourneyEdge): string {
    if (edge.from_node === ENTRY_NODE_ID) return '#2177FF';
    if (!edge.halt_source) return '#2177FF';
    if (edge.resume_on_complete) return '#22c55e';
    return '#f59e0b';
}

function edgeDash(edge: JourneyEdge): string {
    if (!edge.halt_source) return '8 4';
    return 'none';
}

function triggerLabel(trigger: JourneyEdge['trigger']): string {
    switch (trigger.type) {
        case 'campaign_start': return 'on start';
        case 'email_opened': return trigger.step_label ? `opened: ${trigger.step_label} · ${trigger.after_days ?? 0}d` : `opened · ${trigger.after_days ?? 0}d`;
        case 'link_clicked': return trigger.step_label ? `clicked: ${trigger.step_label} · ${trigger.after_days ?? 0}d` : `clicked · ${trigger.after_days ?? 0}d`;
        case 'no_open': return trigger.step_label ? `no open: ${trigger.step_label} · ${trigger.after_days ?? 0}d` : `no open · ${trigger.after_days ?? 0}d`;
        case 'no_click': return trigger.step_label ? `no click: ${trigger.step_label} · ${trigger.after_days ?? 0}d` : `no click · ${trigger.after_days ?? 0}d`;
        case 'sequence_completed': return 'completed';
        // A blank form_id/stage means "match any" (backend semantics — see
        // journey_event_dispatch.py), not "not yet configured" — the caption
        // says so explicitly rather than showing a bare "?".
        case 'lead_form': return trigger.form_name ? `form: ${trigger.form_name}` : (trigger.form_id ? `form: ${trigger.form_id}` : 'form: any');
        case 'crm_lead_stage': return trigger.stage ? `stage: ${trigger.stage}` : 'stage: any';
        default: return trigger.type;
    }
}

// ── Connector position helpers ────────────────────────────────────────────────

function connectorPos(node: JourneyNodeType | null, nodeId: string, entryX: number): { x: number; y: number } {
    if (nodeId === ENTRY_NODE_ID) {
        return { x: entryX, y: 20 + ENTRY_H + 2 + 8 }; // pill bottom + connector offset
    }
    if (!node) return { x: 0, y: 0 };
    const type = (node as any).type ?? 'sequence';
    if (type === 'goal') return { x: (node.position?.x ?? 0) + NODE_WIDTH / 2, y: (node.position?.y ?? 0) + NODE_HEIGHT / 2 };
    return {
        x: (node.position?.x ?? 0) + NODE_WIDTH / 2,
        y: (node.position?.y ?? 0) + NODE_HEIGHT,
    };
}

function targetPos(node: JourneyNodeType | null): { x: number; y: number } {
    if (!node) return { x: 0, y: 0 };
    return {
        x: (node.position?.x ?? 0) + NODE_WIDTH / 2,
        y: (node.position?.y ?? 0),
    };
}

interface Props {
    campaignId?: string;
    journeyId?: string;
    projectId: string;
    hideEDMLink?: boolean;
}

export default function JourneyCanvas({ campaignId, journeyId, projectId, hideEDMLink }: Props) {
    const isCampaignMode = !!campaignId;
    const navigate = useNavigate();
    const [nodes, setNodes] = useState<JourneyNodeType[]>([]);
    const [edges, setEdges] = useState<JourneyEdge[]>([]);
    const [freqCap, setFreqCap] = useState(1);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
    const [showSettings, setShowSettings] = useState(false);
    const [showAnalytics, setShowAnalytics] = useState(false);
    const [analytics, setAnalytics] = useState<Record<string, any> | null>(null);
    const [analyticsLoading, setAnalyticsLoading] = useState(false);
    const [showEventLog, setShowEventLog] = useState(false);
    // 'active' arms crm_lead_stage/lead_form edges; anything else (draft, or
    // not yet loaded) means they're built but silently inert — see the
    // not-armed warning below.
    const [graphStatus, setGraphStatus] = useState<string | null>(null);
    const [showPicker, setShowPicker] = useState(false);
    const [showAddNodeMenu, setShowAddNodeMenu] = useState(false);
    const [pickerSequences, setPickerSequences] = useState<any[]>([]);
    const [pickerLoading, setPickerLoading] = useState(false);
    const [pickerSearch, setPickerSearch] = useState('');
    const [scale, setScale] = useState(1.0);
    const [draggingFrom, setDraggingFrom] = useState<{ nodeId: string; sx: number; sy: number } | null>(null);
    const [ghostPos, setGhostPos] = useState<{ x: number; y: number } | null>(null);
    const canvasRef = useRef<HTMLDivElement>(null);
    const wrapRef = useRef<HTMLDivElement>(null);
    const scaleRef = useRef(1.0);
    const saveDebounce = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    // ── Scale ref (always current for modifier + wheel handler) ──────────────
    useEffect(() => { scaleRef.current = scale; }, [scale]);

    // ── Wheel-to-zoom (cursor-focused, passive:false so preventDefault works) ─
    useEffect(() => {
        const wrap = wrapRef.current;
        if (!wrap) return;
        const onWheel = (e: WheelEvent) => {
            e.preventDefault();
            const prev = scaleRef.current;
            const delta = e.deltaY < 0 ? SCALE_STEP : -SCALE_STEP;
            const next = Math.min(SCALE_MAX, Math.max(SCALE_MIN, prev + delta));
            if (next === prev) return;
            const rect = wrap.getBoundingClientRect();
            const mouseX = e.clientX - rect.left;
            const mouseY = e.clientY - rect.top;
            // Canvas point currently under the cursor
            const canvasX = (wrap.scrollLeft + mouseX) / prev;
            const canvasY = (wrap.scrollTop  + mouseY) / prev;
            setScale(next);
            // After React re-renders with new scale, reposition scroll so the
            // same canvas point stays under the cursor
            requestAnimationFrame(() => {
                if (wrapRef.current) {
                    wrapRef.current.scrollLeft = canvasX * next - mouseX;
                    wrapRef.current.scrollTop  = canvasY * next - mouseY;
                }
            });
        };
        wrap.addEventListener('wheel', onWheel, { passive: false });
        return () => wrap.removeEventListener('wheel', onWheel);
    }, []);

    // ── Load ──────────────────────────────────────────────────────────────────
    useEffect(() => {
        const loader = isCampaignMode
            ? getJourney(campaignId!)
            : getProjectJourney(journeyId!);
        loader.then(data => {
            const cfg: JourneyConfig = data.journey_config || { send_frequency_cap: 1, nodes: [], edges: [] };
            setNodes(cfg.nodes);
            setEdges(cfg.edges);
            setFreqCap(cfg.send_frequency_cap);
            if (!isCampaignMode) setGraphStatus((data as any).status ?? null);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, [campaignId, journeyId, isCampaignMode]);

    // Campaign mode: getJourney() doesn't return the campaign's status, so
    // fetch it separately purely to drive the not-armed warning below.
    useEffect(() => {
        if (!isCampaignMode || !campaignId) return;
        getCampaign(campaignId).then(c => setGraphStatus(c.status)).catch(() => setGraphStatus(null));
    }, [campaignId, isCampaignMode]);

    // ── Persist ───────────────────────────────────────────────────────────────
    const persist = useCallback((n: JourneyNodeType[], e: JourneyEdge[], cap = freqCap) => {
        clearTimeout(saveDebounce.current);
        saveDebounce.current = setTimeout(async () => {
            setSaving(true);
            try {
                if (isCampaignMode) {
                    await saveJourney(campaignId!, { send_frequency_cap: cap, nodes: n, edges: e });
                } else {
                    await saveProjectJourney(journeyId!, { send_frequency_cap: cap, nodes: n, edges: e });
                }
            } catch { /* silent */ } finally {
                setSaving(false);
            }
        }, 300);
    }, [campaignId, journeyId, isCampaignMode, freqCap]);

    // ── dnd-kit sensors ───────────────────────────────────────────────────────
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
    );

    // Divide dnd-kit's screen-pixel transform by scale so nodes track the
    // cursor correctly inside a CSS-scaled container
    const scaleModifier: Modifier = useCallback(({ transform }) => ({
        ...transform,
        x: transform.x / scaleRef.current,
        y: transform.y / scaleRef.current,
    }), []);

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, delta } = event;
        // delta is already in canvas-space (scaleModifier pre-divides by scale)
        setNodes(prev => {
            const moved = prev.map(n =>
                n.id === active.id
                    ? { ...n, position: { x: (n.position?.x ?? 0) + delta.x, y: (n.position?.y ?? 0) + delta.y } }
                    : n
            );
            // If any node went negative, shift all nodes to keep canvas origin at CANVAS_PAD.
            // This lets the canvas grow leftward/upward without negative CSS positions.
            const minX = Math.min(...moved.map(n => n.position?.x ?? 0));
            const minY = Math.min(...moved.map(n => n.position?.y ?? 0));
            const shiftX = minX < CANVAS_PAD ? CANVAS_PAD - minX : 0;
            const shiftY = minY < CANVAS_PAD ? CANVAS_PAD - minY : 0;
            const updated = (shiftX > 0 || shiftY > 0)
                ? moved.map(n => ({ ...n, position: { x: (n.position?.x ?? 0) + shiftX, y: (n.position?.y ?? 0) + shiftY } }))
                : moved;
            persist(updated, edges);
            return updated;
        });
    };

    // ── Canvas dimensions ─────────────────────────────────────────────────────
    const canvasW = Math.max(1200, ...nodes.map(n => (n.position?.x ?? 0) + NODE_WIDTH + CANVAS_PAD));
    const canvasH = Math.max(800, ...nodes.map(n => (n.position?.y ?? 0) + NODE_HEIGHT + CANVAS_PAD));

    // ── Edge drawing (pointer events) ─────────────────────────────────────────
    const handleConnectorPointerDown = useCallback((e: React.PointerEvent, nodeId: string) => {
        e.stopPropagation();
        e.currentTarget.setPointerCapture(e.pointerId);
        const canvasRect = canvasRef.current?.getBoundingClientRect();
        const wrap = wrapRef.current;
        if (!canvasRect || !wrap) return;
        const s = scaleRef.current;
        const sx = (e.clientX - canvasRect.left + wrap.scrollLeft) / s;
        const sy = (e.clientY - canvasRect.top  + wrap.scrollTop)  / s;
        setDraggingFrom({ nodeId, sx, sy });
        setGhostPos({ x: sx, y: sy });
    }, []);

    const handleCanvasPointerMove = (e: React.PointerEvent) => {
        if (!draggingFrom || !canvasRef.current || !wrapRef.current) return;
        const rect = canvasRef.current.getBoundingClientRect();
        const s = scaleRef.current;
        setGhostPos({
            x: (e.clientX - rect.left + wrapRef.current.scrollLeft) / s,
            y: (e.clientY - rect.top  + wrapRef.current.scrollTop)  / s,
        });
    };

    const handleCanvasPointerUp = (e: React.PointerEvent) => {
        if (!draggingFrom || !canvasRef.current || !wrapRef.current) return;
        const rect = canvasRef.current.getBoundingClientRect();
        const s = scaleRef.current;
        const px = (e.clientX - rect.left + wrapRef.current.scrollLeft) / s;
        const py = (e.clientY - rect.top  + wrapRef.current.scrollTop)  / s;

        const targetNode = nodes.find(n => {
            const nx = n.position?.x ?? 0;
            const ny = n.position?.y ?? 0;
            return px >= nx && px <= nx + NODE_WIDTH && py >= ny && py <= ny + NODE_HEIGHT;
        });

        if (targetNode && targetNode.id !== draggingFrom.nodeId) {
            const alreadyExists = edges.some(ed => ed.from_node === draggingFrom.nodeId && ed.to_node === targetNode.id);
            if (!alreadyExists) {
                const newEdge: JourneyEdge = {
                    id: `edge_${Date.now()}`,
                    from_node: draggingFrom.nodeId,
                    to_node: targetNode.id,
                    trigger: { type: 'campaign_start' },
                    halt_source: true,
                    resume_on_complete: false,
                };
                const updated = [...edges, newEdge];
                setEdges(updated);
                persist(nodes, updated);
                setSelectedEdgeId(newEdge.id);
            }
        }
        setDraggingFrom(null);
        setGhostPos(null);
    };

    // ── Entry node pointer down ────────────────────────────────────────────────
    const handleEntryConnectorPointerDown = (e: React.PointerEvent) => {
        e.stopPropagation();
        e.currentTarget.setPointerCapture(e.pointerId);
        const canvasRect = canvasRef.current?.getBoundingClientRect();
        const wrap = wrapRef.current;
        if (!canvasRect || !wrap) return;
        const sx = canvasW / 2;
        const sy = 20 + ENTRY_H + 2 + 8;
        setDraggingFrom({ nodeId: ENTRY_NODE_ID, sx, sy });
        setGhostPos({ x: sx, y: sy });
    };

    // ── Analytics ─────────────────────────────────────────────────────────────
    const toggleAnalytics = async () => {
        if (!isCampaignMode) return;
        if (showAnalytics) {
            setShowAnalytics(false);
            return;
        }
        setShowAnalytics(true);
        if (analytics) return;
        setAnalyticsLoading(true);
        try {
            const data = await getJourneyAnalytics(campaignId!);
            setAnalytics(data.nodes ?? {});
        } catch { setAnalytics({}); }
        finally { setAnalyticsLoading(false); }
    };

    // ── Link sequence ─────────────────────────────────────────────────────────
    const openPicker = async () => {
        setShowPicker(true);
        setPickerLoading(true);
        setShowAddNodeMenu(false);
        try {
            const seqs = await getEDMSequences(projectId);
            // A zero-step "list" sequence (is_list) as a journey node would
            // complete every enrollment instantly on the first tick — exclude.
            setPickerSequences((seqs || []).filter((s: any) => !s.is_list));
        } catch { setPickerSequences([]); }
        finally { setPickerLoading(false); }
    };

    const linkSequence = (seq: any) => {
        if (nodes.some(n => n.edm_sequence_id === seq.sequence_id)) { setShowPicker(false); return; }
        // Default position: grid layout
        const col = nodes.filter(n => !(n as any).type || (n as any).type === 'sequence').length % 3;
        const row = Math.floor(nodes.filter(n => !(n as any).type || (n as any).type === 'sequence').length / 3);
        const newNode: JourneyNodeType = {
            id: `node_${Date.now()}`,
            edm_sequence_id: seq.sequence_id,
            position: { x: 60 + col * (NODE_WIDTH + 60), y: 120 + row * (NODE_HEIGHT + 100) },
            edm_sequence: {
                sequence_id: seq.sequence_id,
                name: seq.name,
                status: seq.status,
                step_count: seq.step_count ?? 0,
            },
        };
        const updated = [...nodes, newNode];
        setNodes(updated);
        setShowPicker(false);
        persist(updated, edges);
    };

    // ── Add special node ──────────────────────────────────────────────────────
    const addSpecialNode = (type: 'ab_split' | 'wait' | 'goal') => {
        const base = { x: 60 + nodes.length * 40, y: 120 + nodes.length * 40 };
        let newNode: any;
        if (type === 'ab_split') {
            newNode = { id: `node_split_${Date.now()}`, type: 'ab_split', splits: [], position: base };
        } else if (type === 'wait') {
            newNode = { id: `node_wait_${Date.now()}`, type: 'wait', delay_days: 3, position: base };
        } else {
            newNode = { id: `node_goal_${Date.now()}`, type: 'goal', conversion_type: 'purchase', position: base };
        }
        const updated = [...nodes, newNode as JourneyNodeType];
        setNodes(updated);
        setShowAddNodeMenu(false);
        persist(updated, edges);
    };

    // ── Unlink node ───────────────────────────────────────────────────────────
    const unlinkNode = (nodeId: string) => {
        if (!window.confirm('Remove this node from the journey?')) return;
        const updatedNodes = nodes.filter(n => n.id !== nodeId);
        const updatedEdges = edges.filter(e => e.from_node !== nodeId && e.to_node !== nodeId);
        setNodes(updatedNodes);
        setEdges(updatedEdges);
        persist(updatedNodes, updatedEdges);
    };

    // ── Edge operations ───────────────────────────────────────────────────────
    const saveEdge = (edge: JourneyEdge) => {
        const updated = edges.map(e => e.id === edge.id ? edge : e);
        setEdges(updated);
        persist(nodes, updated);
        setSelectedEdgeId(null);
    };

    const deleteEdge = (edgeId: string) => {
        const updated = edges.filter(e => e.id !== edgeId);
        setEdges(updated);
        persist(nodes, updated);
        setSelectedEdgeId(null);
    };

    // ── Derived flags ─────────────────────────────────────────────────────────
    const nodeMap = new Map(nodes.map(n => [n.id, n]));
    const selectedEdge = edges.find(e => e.id === selectedEdgeId) ?? null;

    // Entry pill X: center over nodes directly wired from entry; fall back to all-node avg; then canvas center
    const entryLinkedNodes = edges
        .filter(e => e.from_node === ENTRY_NODE_ID)
        .map(e => nodeMap.get(e.to_node))
        .filter((n): n is JourneyNodeType => !!n);
    const entryPillCenterX = entryLinkedNodes.length > 0
        ? entryLinkedNodes.reduce((s, n) => s + (n.position?.x ?? 0) + NODE_WIDTH / 2, 0) / entryLinkedNodes.length
        : nodes.length > 0
            ? nodes.reduce((s, n) => s + (n.position?.x ?? 0) + NODE_WIDTH / 2, 0) / nodes.length
            : canvasW / 2;

    // Nodes that are target of flow-through edges
    const concurrentNodeIds = new Set(
        edges.filter(e => !e.halt_source).map(e => e.to_node)
    );

    // Nodes with 2+ outgoing halt edges (conflict guard warning)
    const haltEdgeCount = new Map<string, number>();
    for (const edge of edges) {
        if (edge.halt_source) {
            haltEdgeCount.set(edge.from_node, (haltEdgeCount.get(edge.from_node) ?? 0) + 1);
        }
    }
    const multiHaltNodeIds = new Set(
        Array.from(haltEdgeCount.entries()).filter(([, c]) => c >= 2).map(([id]) => id)
    );

    const filteredPicker = pickerSequences.filter(s =>
        s.name.toLowerCase().includes(pickerSearch.toLowerCase())
    );

    // ── Ghost edge source coords ───────────────────────────────────────────────
    const ghostFrom = draggingFrom
        ? connectorPos(nodeMap.get(draggingFrom.nodeId) ?? null, draggingFrom.nodeId, entryPillCenterX)
        : null;

    return (
        <>
            {/* Selected edge side panel (outside canvas — fixed position) */}
            {selectedEdge && (
                <JourneyEdgePanel
                    edge={selectedEdge}
                    allNodes={nodes}
                    projectId={projectId}
                    onSave={saveEdge}
                    onDelete={deleteEdge}
                    onClose={() => setSelectedEdgeId(null)}
                />
            )}

            {/* Canvas wrapper */}
            <div className="journey-canvas-wrap" ref={wrapRef}>
                {/* Toolbar */}
                <div className="journey-toolbar">
                    <span className="journey-toolbar-title">Journey Builder</span>
                    {saving && (
                        <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
                            Saving…
                        </span>
                    )}
                    <div className="journey-toolbar-spacer" />

                    {/* Add node menu */}
                    <div style={{ position: 'relative' }}>
                        <GlassButton variant="primary" onClick={() => setShowAddNodeMenu(v => !v)}>
                            <Plus size={15} />
                            Add Node
                        </GlassButton>
                        {showAddNodeMenu && (
                            <div style={{
                                position: 'absolute', top: '100%', right: 0, marginTop: 6,
                                background: 'var(--panel)', border: '1px solid var(--stroke)',
                                borderRadius: 10, padding: 6, minWidth: 180, zIndex: 50,
                                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                            }}>
                                {[
                                    { label: 'EDM Sequence', icon: <Mail size={14} />, action: openPicker },
                                    { label: 'A/B Split', icon: <GitBranch size={14} />, action: () => addSpecialNode('ab_split') },
                                    { label: 'Wait / Delay', icon: <Clock size={14} />, action: () => addSpecialNode('wait') },
                                    { label: 'Goal Node', icon: <CheckCircle2 size={14} />, action: () => addSpecialNode('goal') },
                                ].map(item => (
                                    <button
                                        key={item.label}
                                        onClick={item.action}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: 8,
                                            width: '100%', padding: '8px 12px',
                                            background: 'none', border: 'none', cursor: 'pointer',
                                            color: 'var(--text)', fontSize: '0.85rem', borderRadius: 6,
                                            transition: 'background 0.1s',
                                        }}
                                        onMouseEnter={e => (e.currentTarget.style.background = 'var(--panel-strong, rgba(0,0,0,0.06))')}
                                        onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                                    >
                                        {item.icon}
                                        {item.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {isCampaignMode && (
                        <GlassButton
                            variant="secondary"
                            onClick={toggleAnalytics}
                            className={showAnalytics ? 'journey-analytics-active' : ''}
                            title="Toggle analytics overlay"
                        >
                            <BarChart2 size={15} />
                            {analyticsLoading ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : 'Analytics'}
                        </GlassButton>
                    )}

                    <GlassButton
                        variant="secondary"
                        onClick={() => setShowEventLog(v => !v)}
                        className={showEventLog ? 'journey-analytics-active' : ''}
                        title="Event log — did a CRM/lead-form trigger fire, and if not why not"
                    >
                        <ScrollText size={15} />
                        Event Log
                    </GlassButton>

                    <GlassButton variant="secondary" onClick={() => { setShowSettings(v => !v); setShowAddNodeMenu(false); }} title="Journey settings">
                        <Settings size={15} />
                    </GlassButton>

                    {!hideEDMLink && (
                        <GlassButton variant="secondary" onClick={() => navigate('/edm?tab=sequences')} title="Open Email Marketing">
                            <ExternalLink size={14} />
                            Email Marketing
                        </GlassButton>
                    )}

                    {/* Zoom controls */}
                    <div className="journey-zoom-controls">
                        <button
                            className="journey-zoom-btn"
                            title="Zoom out"
                            onClick={() => setScale(s => Math.max(SCALE_MIN, +(s - SCALE_STEP).toFixed(2)))}
                        >
                            <ZoomOut size={14} />
                        </button>
                        <button
                            className="journey-zoom-label"
                            title="Reset zoom"
                            onClick={() => setScale(1.0)}
                        >
                            {Math.round(scale * 100)}%
                        </button>
                        <button
                            className="journey-zoom-btn"
                            title="Zoom in"
                            onClick={() => setScale(s => Math.min(SCALE_MAX, +(s + SCALE_STEP).toFixed(2)))}
                        >
                            <ZoomIn size={14} />
                        </button>
                    </div>

                    {/* Settings panel */}
                    {showSettings && (
                        <div className="journey-settings-panel">
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <p className="journey-settings-title">Journey Settings</p>
                                <button onClick={() => setShowSettings(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}>
                                    <X size={16} />
                                </button>
                            </div>
                            <div className="journey-settings-row">
                                <label>Max emails / recipient / day</label>
                                <select
                                    className="journey-select"
                                    value={freqCap}
                                    onChange={e => {
                                        const cap = Number(e.target.value);
                                        setFreqCap(cap);
                                        persist(nodes, edges, cap);
                                    }}
                                >
                                    {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}
                                </select>
                                <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                                    Applies across all active sequences in this journey.
                                </span>
                            </div>
                        </div>
                    )}

                    {/* Event log panel */}
                    {showEventLog && (
                        <div className="journey-settings-panel">
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                                <p className="journey-settings-title">Trigger Event Log</p>
                                <button onClick={() => setShowEventLog(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}>
                                    <X size={16} />
                                </button>
                            </div>
                            <JourneyEventLog campaignId={isCampaignMode ? campaignId : undefined} journeyId={!isCampaignMode ? journeyId : undefined} />
                        </div>
                    )}
                </div>

                {/* Not-armed warning — the single most likely support question this
                    feature creates: a correctly-built crm_lead_stage/lead_form edge
                    on a non-active graph is, correctly, inert. */}
                {graphStatus !== null && graphStatus !== 'active' && edges.some(e => e.trigger.type === 'crm_lead_stage' || e.trigger.type === 'lead_form') && (
                    <div className="journey-flow-warning" style={{ margin: '0 16px 12px' }}>
                        <AlertTriangle size={13} style={{ display: 'inline', marginRight: 5 }} />
                        This journey has a CRM lead-stage or lead-form trigger, but the {isCampaignMode ? 'campaign' : 'journey'} isn't active —
                        these edges will not fire until you activate it.
                    </div>
                )}

                {/* Scroll spacer — drives scrollbar dimensions at current scale */}
                <div style={{ width: canvasW * scale, height: canvasH * scale, position: 'relative', flexShrink: 0 }}>

                {/* SVG + Node layer — scaled transform */}
                <div
                    ref={canvasRef}
                    className="journey-canvas"
                    style={{
                        width: canvasW, height: canvasH,
                        transform: `scale(${scale})`, transformOrigin: '0 0',
                        position: 'absolute', top: 0, left: 0,
                    }}
                    onPointerMove={handleCanvasPointerMove}
                    onPointerUp={handleCanvasPointerUp}
                    onClick={() => { setShowSettings(false); setShowAddNodeMenu(false); }}
                >
                    {/* Loading state */}
                    {loading && (
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: 'var(--text-secondary)' }}>
                            <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                            Loading journey…
                        </div>
                    )}

                    {/* Empty state */}
                    {!loading && nodes.length === 0 && (
                        <div className="journey-canvas-empty">
                            <Mail size={40} style={{ opacity: 0.3 }} />
                            <p>No sequences yet. Add an EDM sequence to start building your journey.</p>
                            <div style={{ pointerEvents: 'all' }}>
                                <GlassButton variant="primary" onClick={openPicker}>
                                    <Plus size={15} />
                                    Link Sequence
                                </GlassButton>
                            </div>
                        </div>
                    )}

                    {/* SVG overlay */}
                    <svg className="journey-canvas-svg" style={{ width: canvasW, height: canvasH }}>
                        <defs>
                            <marker id="arrow-amber" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                                <polygon points="0 0, 10 3.5, 0 7" fill="#f59e0b" />
                            </marker>
                            <marker id="arrow-blue" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                                <polygon points="0 0, 10 3.5, 0 7" fill="#2177FF" />
                            </marker>
                            <marker id="arrow-green" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                                <polygon points="0 0, 10 3.5, 0 7" fill="#22c55e" />
                            </marker>
                        </defs>

                        {edges.map(edge => {
                            const fromNode = nodeMap.get(edge.from_node) ?? null;
                            const toNode = nodeMap.get(edge.to_node) ?? null;
                            if (!toNode) return null;

                            const from = connectorPos(fromNode, edge.from_node, entryPillCenterX);
                            const to = targetPos(toNode);
                            const path = bezierPath(from.x, from.y, to.x, to.y);
                            const mid = midpoint(from.x, from.y, to.x, to.y);
                            const colour = edgeColour(edge);
                            const dash = edgeDash(edge);
                            const markerSuffix = colour === '#f59e0b' ? 'amber' : colour === '#22c55e' ? 'green' : 'blue';
                            const isSelected = edge.id === selectedEdgeId;
                            const label = triggerLabel(edge.trigger);

                            return (
                                <g key={edge.id}>
                                    {/* Invisible wide hit area */}
                                    <path
                                        d={path}
                                        className="journey-edge-path--hit"
                                        onClick={() => setSelectedEdgeId(isSelected ? null : edge.id)}
                                    />
                                    {/* Visible path */}
                                    <path
                                        d={path}
                                        className="journey-edge-path"
                                        stroke={colour}
                                        strokeWidth={isSelected ? 3 : 2}
                                        strokeDasharray={dash}
                                        fill="none"
                                        markerEnd={`url(#arrow-${markerSuffix})`}
                                        opacity={isSelected ? 1 : 0.75}
                                        onClick={() => setSelectedEdgeId(isSelected ? null : edge.id)}
                                    />
                                    {/* Midpoint label */}
                                    <g className={`journey-edge-label${isSelected ? ' journey-edge-label--selected' : ''}`} transform={`translate(${mid.x}, ${mid.y})`}>
                                        <rect x={-34} y={-10} width={68} height={20} rx={4} />
                                        <text x={0} y={4} textAnchor="middle">{label}</text>
                                    </g>
                                </g>
                            );
                        })}

                        {/* Ghost edge while drawing */}
                        {draggingFrom && ghostFrom && ghostPos && (
                            <path
                                className="journey-ghost-edge"
                                d={bezierPath(ghostFrom.x, ghostFrom.y, ghostPos.x, ghostPos.y)}
                            />
                        )}
                    </svg>

                    {/* Entry node */}
                    <div
                        className="journey-entry-node"
                        style={{ left: entryPillCenterX - ENTRY_W / 2, top: 20 }}
                    >
                        <div className="journey-entry-pill" onClick={() => setShowSettings(true)}>
                            ▶ {isCampaignMode ? 'Campaign Start' : 'Journey Start'}
                        </div>
                        <div
                            className="journey-entry-connector"
                            onPointerDown={handleEntryConnectorPointerDown}
                            title="Drag to connect to a sequence"
                        />
                    </div>

                    {/* Draggable sequence/special nodes */}
                    <DndContext sensors={sensors} modifiers={[scaleModifier]} onDragEnd={handleDragEnd}>
                        {nodes.map(node => (
                            <JourneyNodeComponent
                                key={node.id}
                                node={node}
                                allNodes={nodes}
                                isSelected={selectedEdgeId ? edges.find(e => e.id === selectedEdgeId)?.from_node === node.id || edges.find(e => e.id === selectedEdgeId)?.to_node === node.id : false}
                                isConcurrent={concurrentNodeIds.has(node.id)}
                                hasMultipleHalt={multiHaltNodeIds.has(node.id)}
                                analyticsData={analytics?.[node.id]}
                                showAnalytics={showAnalytics}
                                onConnectorPointerDown={handleConnectorPointerDown}
                                onUnlink={unlinkNode}
                                onEditEDM={seqId => navigate(`/edm?sequence=${seqId}`)}
                            />
                        ))}
                    </DndContext>
                </div>
                </div>{/* end scroll spacer */}
            </div>

            {/* Sequence picker modal */}
            {showPicker && (
                <div className="journey-picker-overlay" onClick={() => setShowPicker(false)}>
                    <div className="journey-picker-modal" onClick={e => e.stopPropagation()}>
                        <div className="journey-picker-header">
                            <h3>Link EDM Sequence</h3>
                            <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }} onClick={() => setShowPicker(false)}>
                                <X size={18} />
                            </button>
                        </div>

                        <div className="journey-picker-search">
                            <Search size={15} className="journey-picker-search-icon" />
                            <input
                                type="text"
                                placeholder="Search sequences…"
                                value={pickerSearch}
                                onChange={e => setPickerSearch(e.target.value)}
                                autoFocus
                            />
                        </div>

                        <div className="journey-picker-list">
                            {pickerLoading ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-secondary)', padding: 16 }}>
                                    <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                                    Loading…
                                </div>
                            ) : filteredPicker.length === 0 ? (
                                <p style={{ color: 'var(--text-secondary)', padding: 12, textAlign: 'center' }}>
                                    No sequences found.{' '}
                                    <button
                                        style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', padding: 0, fontSize: 'inherit' }}
                                        onClick={() => navigate('/edm?tab=sequences')}
                                    >
                                        Create one in EDM →
                                    </button>
                                </p>
                            ) : (
                                filteredPicker.map(seq => {
                                    const linked = nodes.some(n => n.edm_sequence_id === seq.sequence_id);
                                    return (
                                        <div
                                            key={seq.sequence_id}
                                            className={`journey-picker-item${linked ? ' journey-picker-item--disabled' : ''}`}
                                            onClick={() => !linked && linkSequence(seq)}
                                        >
                                            <div>
                                                <div style={{ fontWeight: 600, color: 'var(--text)', fontSize: '0.88rem' }}>{seq.name}</div>
                                                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 2 }}>
                                                    {seq.step_count ?? 0} steps · {seq.enrolled_count ?? 0} enrolled
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                                                {linked && <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>Linked</span>}
                                                <GlassBadge variant={seq.status === 'active' ? 'success' : 'default'}>
                                                    {seq.status}
                                                </GlassBadge>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        <div className="journey-picker-footer">
                            <GlassButton variant="secondary" onClick={() => setShowPicker(false)}>Cancel</GlassButton>
                            <GlassButton variant="secondary" onClick={() => navigate('/edm?tab=sequences')}>
                                <ExternalLink size={14} />
                                Create in EDM
                            </GlassButton>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
